/**
 * The node's gathering (backend: pods/gatherings), from either side.
 *
 *   const member = useMemberGathering({ pollMs });
 *   //   { data, loading, error, saving, refresh, setMyGroups(ids) }
 *   const admin = useAdminGathering({ enabled });
 *   //   { data, loading, error, refresh, updateSettings, createGroup,
 *   //     updateGroup, deleteGroup, createTopic, updateTopic, deleteTopic,
 *   //     reorderTopics, replaceFlow, startFlow, stopFlow, pauseFlow,
 *   //     resumeFlow, extendStep, nextStep, importConfig, resetGathering,
 *   //     clearPicks, removeGuests(scope), publish, unpublish }
 *   const { people } = useNodePeople({ enabled, includePending });
 *   //   every person the admin can seat when the pool is "Everyone"
 *   const status = useGatheringStatus({ enabled });
 *   //   'closed' | 'waiting' | 'open' | null (unknown yet) — for chrome
 *   //   like the nav bar that only needs to know whether it's live
 *
 * The member view re-polls (every 15s by default; the live flow page asks
 * for faster) so a flow started, paused or moved on by the admin shows up
 * without a reload. It acts as useGatheringIdentity() — a
 * guest who joined with the passcode, else the signed-in user — and sends a
 * guest whose pass the backend refuses back to /gathering/join.
 *
 * Member picks are applied optimistically — a checkbox shouldn't wait on a
 * round trip — and rolled back if the save fails. Admin mutations resolve to
 * the server's state and replace `data` with it.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { useGatheringIdentity } from './use_gathering_identity';
import { clearGuest } from '../utils/guest_session';
import { useUnclaimedPeople } from './use_unclaimed_people';
import { parseServerTime } from '../utils/gathering';

export function useMemberGathering({ pollMs = 15000 } = {}) {
    const api = useApi();
    const navigate = useNavigate();
    const { userId, isGuest } = useGatheringIdentity();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        if (!userId) return Promise.resolve();
        return api.get('/api/gathering', { params: { user_id: userId } })
            .then(d => {
                // A guest the host removed has no person any more: the
                // backend can't tell that id from any other unknown one, so
                // the page has to notice and send them back to the door.
                if (isGuest && d && d.my_person_id == null) {
                    clearGuest();
                    navigate('/gathering/join?removed=1', { replace: true });
                    return;
                }
                setData(d);
                setError(null);
            })
            .catch(err => {
                // The pass ran out (or the guest was reaped) mid-gathering.
                if (isGuest && err.status === 403) {
                    clearGuest();
                    navigate('/gathering/join?expired=1', { replace: true });
                    return;
                }
                setError(err.message);
            })
            .finally(() => setLoading(false));
    }, [api, userId, isGuest, navigate]);

    useEffect(() => { load(); }, [load]);

    // The admin starts and stops the flow from their own screen, so poll —
    // gently — to notice. The countdown itself ticks locally.
    useEffect(() => {
        const timer = setInterval(() => { if (!document.hidden) load(); }, pollMs);
        return () => clearInterval(timer);
    }, [load, pollMs]);

    const setMyGroups = useCallback((groupIds) => {
        const before = data;
        setData(d => (d ? { ...d, my_group_ids: groupIds } : d));
        setSaving(true);
        return api.put('/api/gathering/me', { group_ids: groupIds }, { params: { user_id: userId } })
            .then(setData)
            .catch(err => { setData(before); message.error(err.message || 'Could not save your groups'); })
            .finally(() => setSaving(false));
    }, [api, userId, data]);

    return { data, loading, error, saving, refresh: load, setMyGroups };
}

export function useAdminGathering({ enabled = true } = {}) {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const params = { params: { user_id: userId } };

    const load = useCallback(() => {
        if (!enabled || !userId) { setLoading(false); return Promise.resolve(); }
        return api.get('/api/gathering/admin', { params: { user_id: userId } })
            .then(d => { setData(d); setError(null); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [api, userId, enabled]);

    useEffect(() => { load(); }, [load]);

    // Every admin write either returns the whole admin state (settings, rooms)
    // or touches groups, after which a reload keeps memberships consistent.
    const wrap = (promise, { reload = false } = {}) => promise
        .then(d => (reload ? load().then(() => d) : (setData(d), d)))
        .catch(err => { message.error(err.message || 'Something went wrong'); throw err; });

    return {
        data,
        loading,
        error,
        refresh: load,
        updateSettings: patch => wrap(api.patch('/api/gathering/admin', patch, params)),
        createGroup: body => wrap(api.post('/api/gathering/admin/groups', body, params), { reload: true }),
        // A rename or seat change touches one row — patch it in place rather
        // than reloading, so typing into the ideal room stays smooth.
        updateGroup: (id, patch) => api.patch(`/api/gathering/admin/groups/${id}`, patch, params)
            .then(g => { setData(d => (d ? { ...d, groups: d.groups.map(x => (x.id === g.id ? g : x)) } : d)); return g; })
            .catch(err => { message.error(err.message || 'Could not save the group'); throw err; }),
        deleteGroup: id => wrap(api.del(`/api/gathering/admin/groups/${id}`, params), { reload: true }),
        // Topics: each write patches `data.topics` in place.
        createTopic: text => api.post('/api/gathering/admin/topics', { text }, params)
            .then(t => { setData(d => (d ? { ...d, topics: [...d.topics, t] } : d)); return t; })
            .catch(err => { message.error(err.message || 'Could not add the topic'); throw err; }),
        updateTopic: (id, text) => api.patch(`/api/gathering/admin/topics/${id}`, { text }, params)
            .then(t => { setData(d => (d ? { ...d, topics: d.topics.map(x => (x.id === t.id ? t : x)) } : d)); return t; })
            .catch(err => { message.error(err.message || 'Could not save the topic'); throw err; }),
        deleteTopic: id => api.del(`/api/gathering/admin/topics/${id}`, params)
            .then(() => setData(d => (d ? { ...d, topics: d.topics.filter(x => x.id !== id) } : d)))
            .catch(err => { message.error(err.message || 'Could not delete the topic'); throw err; }),
        reorderTopics: ids => {
            // Optimistic: moving a row shouldn't wait on the network.
            setData(d => (d ? { ...d, topics: ids.map(id => d.topics.find(t => t.id === id)).filter(Boolean) } : d));
            return api.put('/api/gathering/admin/topics/order', { ids }, params)
                .then(topics => setData(d => (d ? { ...d, topics } : d)))
                .catch(err => { message.error(err.message || 'Could not reorder topics'); load(); throw err; });
        },
        // steps: [{ step_id, rooms: [{ person_ids, quota }] }] — every breakout round at once.
        publish: steps => wrap(api.put('/api/gathering/admin/rooms', { steps }, params)),
        // steps: [{ id?, kind, duration_minutes, seed }] — the whole flow, in order.
        replaceFlow: steps => wrap(api.put('/api/gathering/admin/flow', { steps }, params)),
        startFlow: () => wrap(api.post('/api/gathering/admin/flow/start', undefined, params)),
        stopFlow: () => wrap(api.post('/api/gathering/admin/flow/stop', undefined, params)),
        pauseFlow: () => wrap(api.post('/api/gathering/admin/flow/pause', undefined, params)),
        resumeFlow: () => wrap(api.post('/api/gathering/admin/flow/resume', undefined, params)),
        resetGathering: () => wrap(api.post('/api/gathering/admin/reset', undefined, params)),
        // Everyone's picks go, groups stay — members pick again.
        clearPicks: () => wrap(api.del('/api/gathering/admin/picks', params)),
        // scope: 'expired' (tidy up passes that ran out) | 'all' (end every guest pass now)
        removeGuests: scope => wrap(api.post('/api/gathering/admin/guests/remove', { scope }, params)),
        importConfig: config => wrap(api.post('/api/gathering/admin/import', config, params)),
        nextStep: stepId => wrap(api.post('/api/gathering/admin/flow/next', { step_id: stepId }, params)),
        extendStep: (stepId, minutes) => wrap(api.post('/api/gathering/admin/flow/extend', { step_id: stepId, minutes }, params)),
        unpublish: () => wrap(api.del('/api/gathering/admin/rooms', params)),
    };
}

/**
 * Every person on the node, for an admin seating "Everyone": listed people,
 * plus the unlisted ones a superadmin can see (same sources as Browse).
 * People with no account (user_id null) couldn't join a call, so they're
 * left out unless asked for.
 */
export function useNodePeople({ enabled = true, includePending = false } = {}) {
    const api = useApi();
    const { allPeople } = useUnclaimedPeople();
    const [listed, setListed] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!enabled) { setLoading(false); return undefined; }
        let alive = true;
        api.get('/api/entities', { params: { listed: true, entity_type: 'person' } })
            .then(d => { if (alive) setListed(Array.isArray(d) ? d : []); })
            .catch(() => { if (alive) setListed([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [api, enabled]);

    const people = useMemo(() => {
        const byId = new Map(listed.filter(e => e.entity_type === 'person').map(e => [e.id, e]));
        allPeople.forEach(p => { if (!byId.has(p.id)) byId.set(p.id, p); });
        return [...byId.values()].filter(p => includePending || p.user_id);
    }, [listed, allPeople, includePending]);

    return { people, loading };
}

/**
 * Just the gathering's status, polled for chrome that only needs to know
 * whether it's live (the nav bar) — not the groups/schedule payload that
 * comes with it. Same endpoint and poll cadence as useMemberGathering, but
 * without its guest-session/redirect plumbing, since that chrome never
 * renders for a guest (GatheringPage has no NavBar — see RouteMap.js).
 */
export function useGatheringStatus({ enabled = true, pollMs = 15000 } = {}) {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!enabled || !userId) return undefined;
        let alive = true;
        const load = () => api.get('/api/gathering', { params: { user_id: userId } })
            .then(d => { if (alive) setStatus(d?.status ?? null); })
            .catch(() => {});
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, pollMs);
        return () => { alive = false; clearInterval(timer); };
    }, [api, userId, enabled, pollMs]);

    return status;
}

/**
 * "Now" in ms on the server's clock (from a response's `server_now`), ticking
 * every second while `active` — so a countdown is right even when the
 * viewer's clock isn't.
 */
export function useServerClock(serverNow, active) {
    const offset = useMemo(() => (serverNow ? parseServerTime(serverNow) - Date.now() : 0), [serverNow]);
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [active]);
    return now + offset;
}
